import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import {
  collection, doc, onSnapshot, deleteDoc, updateDoc,
  setDoc, query, orderBy, limit, getDoc, serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

export const ADMIN_EMAIL = 'abelxmendoza@gmail.com';
export const isAdminUser = email => email === ADMIN_EMAIL;

const today = () => new Date().toISOString().slice(0, 10);

const AdminConsole = ({ user, onClose, isOpen }) => {
  const [section, setSection] = useState('mod');
  const [comments, setComments] = useState([]);
  const [caption, setCaption] = useState('');
  const [captionSaved, setCaptionSaved] = useState(false);
  const [adminReplies, setAdminReplies] = useState({});
  const [stats, setStats] = useState({ total: 0, today: 0 });
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!isOpen) return;
    const q = query(collection(db, 'comments'), orderBy('timestamp', 'desc'), limit(50));
    return onSnapshot(q, snap => {
      setComments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, [isOpen]);

  // Load caption
  useEffect(() => {
    if (!isOpen) return;
    return onSnapshot(doc(db, 'vibeCheck', 'caption'), snap => {
      setCaption(snap.exists() ? (snap.data()?.text || '') : '');
    });
  }, [isOpen]);

  // Load stats
  useEffect(() => {
    if (!isOpen) return;
    const unsubTotal = onSnapshot(doc(db, 'meta', 'commentStats'), snap => {
      setStats(s => ({ ...s, total: snap.data()?.total ?? 0 }));
    });
    getDoc(doc(db, 'vibeCheckHistory', `day-${today()}`)).then(snap => {
      if (!snap.exists()) return;
      const d = snap.data() ?? {};
      const sum = Object.values(d).reduce((a, v) => a + (typeof v === 'number' ? v : 0), 0);
      setStats(s => ({ ...s, today: sum }));
    }).catch(() => {});
    return unsubTotal;
  }, [isOpen]);

  const adminDelete = async id => {
    if (!window.confirm('Delete this comment?')) return;
    await deleteDoc(doc(db, 'comments', id)).catch(() => {});
  };

  const postAdminReply = async commentId => {
    const text = (adminReplies[commentId] || '').trim();
    if (!text) return;
    const comment = comments.find(c => c.id === commentId);
    if (!comment) return;
    const reply = {
      text,
      authorId: user.uid,
      authorName: 'Abel',
      authorPhoto: user.photoURL || null,
      isAdminReply: true,
      timestamp: Timestamp.now(),
      isAnonymous: false,
    };
    await updateDoc(doc(db, 'comments', commentId), {
      replies: [...(comment.replies || []), reply],
    });
    setAdminReplies(prev => ({ ...prev, [commentId]: '' }));
  };

  const saveCaption = async () => {
    await setDoc(doc(db, 'vibeCheck', 'caption'), { text: caption });
    setCaptionSaved(true);
    setTimeout(() => setCaptionSaved(false), 2000);
  };

  const shown = filter === 'questions'
    ? comments.filter(c => c.isQuestion)
    : comments;

  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <Panel onClick={e => e.stopPropagation()}>

        <PanelHeader>
          <PanelTitle>👑 Admin Console</PanelTitle>
          <CloseBtn onClick={onClose}>✕</CloseBtn>
        </PanelHeader>

        {/* Stats row */}
        <StatsRow>
          <StatBox>
            <StatNum>{stats.total}</StatNum>
            <StatLabel>Total Comments</StatLabel>
          </StatBox>
          <StatBox>
            <StatNum>{comments.filter(c => c.isQuestion).length}</StatNum>
            <StatLabel>Questions</StatLabel>
          </StatBox>
          <StatBox>
            <StatNum>{stats.today}</StatNum>
            <StatLabel>Vibe Votes Today</StatLabel>
          </StatBox>
        </StatsRow>

        {/* Section tabs */}
        <SectionTabs>
          <STab $active={section === 'mod'} onClick={() => setSection('mod')}>🗑️ Moderation</STab>
          <STab $active={section === 'caption'} onClick={() => setSection('caption')}>⚡ Vibe Caption</STab>
        </SectionTabs>

        {section === 'mod' && (
          <>
            <FilterRow>
              <FilterBtn $active={filter === 'all'} onClick={() => setFilter('all')}>
                All ({comments.length})
              </FilterBtn>
              <FilterBtn $active={filter === 'questions'} onClick={() => setFilter('questions')}>
                Questions ({comments.filter(c => c.isQuestion).length})
              </FilterBtn>
            </FilterRow>

            <CommentList>
              {shown.length === 0 && <EmptyNote>Nothing here.</EmptyNote>}
              {shown.map(c => (
                <AdminCommentRow key={c.id}>
                  <RowMeta>
                    <RowAuthor>{c.authorName}</RowAuthor>
                    {c.isQuestion && <QuestionTag>❓</QuestionTag>}
                    <RowTime>
                      {c.timestamp?.toDate?.().toLocaleDateString() || 'now'}
                    </RowTime>
                    <AdminDeleteBtn onClick={() => adminDelete(c.id)} title="Delete">🗑️</AdminDeleteBtn>
                  </RowMeta>
                  <RowText>{c.text?.slice(0, 120) || <i>media/audio post</i>}</RowText>
                  {/* Admin reply input — visible for all, most useful for questions */}
                  <ReplyRow>
                    <AdminReplyInput
                      placeholder={c.isQuestion ? 'Answer this question...' : 'Reply as Abel...'}
                      value={adminReplies[c.id] || ''}
                      onChange={e => setAdminReplies(p => ({ ...p, [c.id]: e.target.value }))}
                      onKeyDown={e => e.key === 'Enter' && !e.shiftKey && postAdminReply(c.id)}
                    />
                    <AdminReplyBtn
                      disabled={!(adminReplies[c.id] || '').trim()}
                      onClick={() => postAdminReply(c.id)}
                    >
                      Reply
                    </AdminReplyBtn>
                  </ReplyRow>
                </AdminCommentRow>
              ))}
            </CommentList>
          </>
        )}

        {section === 'caption' && (
          <CaptionSection>
            <CaptionLabel>
              Weekly Vibe Check caption — shows above the vote options for everyone.
            </CaptionLabel>
            <CaptionInput
              value={caption}
              onChange={e => setCaption(e.target.value)}
              placeholder='e.g. "just got back from tour, be honest"'
              maxLength={120}
            />
            <CaptionFooter>
              <CharCount>{caption.length}/120</CharCount>
              <SaveCaptionBtn onClick={saveCaption}>
                {captionSaved ? '✅ Saved!' : 'Save Caption'}
              </SaveCaptionBtn>
            </CaptionFooter>
            {caption && (
              <CaptionPreview>Preview: <em>"{caption}"</em></CaptionPreview>
            )}
            <ClearCaptionBtn onClick={() => { setCaption(''); setDoc(doc(db, 'vibeCheck', 'caption'), { text: '' }); }}>
              Clear caption
            </ClearCaptionBtn>
          </CaptionSection>
        )}

      </Panel>
    </Overlay>
  );
};

export default AdminConsole;

// ── styles ────────────────────────────────────────────────────────────────────

const slideIn = keyframes`
  from { transform: translateX(100%); opacity: 0; }
  to   { transform: translateX(0);    opacity: 1; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.55);
  z-index: 1200;
  display: flex;
  justify-content: flex-end;
`;

const Panel = styled.div`
  width: 420px;
  max-width: 100vw;
  height: 100%;
  background: rgba(8, 0, 14, 0.98);
  border-left: 2px solid rgba(148,0,211,0.5);
  display: flex;
  flex-direction: column;
  animation: ${slideIn} 0.25s ease both;
  overflow: hidden;
  @media (max-width: 430px) { width: 100vw; }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  border-bottom: 1px solid rgba(148,0,211,0.3);
  background: rgba(76,0,101,0.4);
  flex-shrink: 0;
`;

const PanelTitle = styled.h2`
  color: #cc44ff;
  font-family: 'Creepster', Arial, sans-serif;
  font-size: 1.4rem;
  margin: 0;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: rgba(255,255,255,0.5);
  font-size: 1.1rem;
  cursor: pointer;
  padding: 4px 8px;
  &:hover { color: white; }
`;

const StatsRow = styled.div`
  display: flex;
  gap: 1px;
  background: rgba(148,0,211,0.2);
  border-bottom: 1px solid rgba(148,0,211,0.25);
  flex-shrink: 0;
`;

const StatBox = styled.div`
  flex: 1;
  padding: 12px 8px;
  text-align: center;
  background: rgba(0,0,0,0.4);
`;

const StatNum = styled.div`
  font-family: 'Creepster', Arial, sans-serif;
  font-size: 1.5rem;
  color: #cc44ff;
`;

const StatLabel = styled.div`
  font-size: 0.65rem;
  color: rgba(255,255,255,0.4);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SectionTabs = styled.div`
  display: flex;
  border-bottom: 1px solid rgba(148,0,211,0.25);
  flex-shrink: 0;
`;

const STab = styled.button`
  flex: 1;
  padding: 10px;
  background: ${({ $active }) => $active ? 'rgba(76,0,101,0.5)' : 'transparent'};
  border: none;
  border-bottom: 2px solid ${({ $active }) => $active ? '#cc44ff' : 'transparent'};
  color: ${({ $active }) => $active ? 'white' : 'rgba(255,255,255,0.45)'};
  font-family: 'Creepster', Arial, sans-serif;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { color: white; background: rgba(76,0,101,0.3); }
`;

const FilterRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  flex-shrink: 0;
`;

const FilterBtn = styled.button`
  padding: 4px 12px;
  border-radius: 12px;
  border: 1px solid ${({ $active }) => $active ? 'rgba(148,0,211,0.7)' : 'rgba(255,255,255,0.15)'};
  background: ${({ $active }) => $active ? 'rgba(76,0,101,0.5)' : 'transparent'};
  color: ${({ $active }) => $active ? 'white' : 'rgba(255,255,255,0.45)'};
  font-family: 'Creepster', Arial, sans-serif;
  font-size: 0.82rem;
  cursor: pointer;
`;

const CommentList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AdminCommentRow = styled.div`
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 8px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const RowMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const RowAuthor = styled.span`
  font-family: 'Creepster', Arial, sans-serif;
  font-size: 0.9rem;
  color: rgba(255,255,255,0.8);
  flex: 1;
`;

const QuestionTag = styled.span`font-size: 0.85rem;`;

const RowTime = styled.span`
  font-size: 0.68rem;
  color: rgba(255,255,255,0.3);
`;

const AdminDeleteBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.9rem;
  opacity: 0.5;
  padding: 2px 4px;
  border-radius: 4px;
  transition: opacity 0.15s, background 0.15s;
  &:hover { opacity: 1; background: rgba(139,0,0,0.4); }
`;

const RowText = styled.p`
  margin: 0;
  font-size: 0.83rem;
  color: rgba(255,255,255,0.55);
  line-height: 1.4;
`;

const ReplyRow = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 2px;
`;

const AdminReplyInput = styled.input`
  flex: 1;
  padding: 6px 10px;
  background: rgba(0,0,0,0.4);
  border: 1px solid rgba(148,0,211,0.3);
  border-radius: 6px;
  color: white;
  font-family: 'Creepster', Arial, sans-serif;
  font-size: 0.82rem;
  outline: none;
  &::placeholder { color: rgba(255,255,255,0.25); }
  &:focus { border-color: rgba(148,0,211,0.7); }
`;

const AdminReplyBtn = styled.button`
  padding: 6px 12px;
  background: rgba(76,0,101,0.7);
  border: 1px solid rgba(148,0,211,0.5);
  border-radius: 6px;
  color: white;
  font-family: 'Creepster', Arial, sans-serif;
  font-size: 0.82rem;
  cursor: pointer;
  white-space: nowrap;
  &:hover:not(:disabled) { background: rgba(76,0,101,1); }
  &:disabled { opacity: 0.3; cursor: default; }
`;

const EmptyNote = styled.p`
  text-align: center;
  color: rgba(255,255,255,0.2);
  font-size: 0.85rem;
  margin-top: 32px;
`;

const CaptionSection = styled.div`
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CaptionLabel = styled.p`
  color: rgba(255,255,255,0.5);
  font-size: 0.85rem;
  margin: 0;
  line-height: 1.4;
`;

const CaptionInput = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  background: rgba(0,0,0,0.4);
  border: 1.5px solid rgba(148,0,211,0.4);
  border-radius: 8px;
  color: white;
  font-family: 'Creepster', Arial, sans-serif;
  font-size: 0.95rem;
  resize: vertical;
  min-height: 80px;
  outline: none;
  box-sizing: border-box;
  &::placeholder { color: rgba(255,255,255,0.25); }
  &:focus { border-color: rgba(148,0,211,0.8); }
`;

const CaptionFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CharCount = styled.span`
  font-size: 0.75rem;
  color: rgba(255,255,255,0.3);
`;

const SaveCaptionBtn = styled.button`
  padding: 8px 20px;
  background: rgba(76,0,101,0.7);
  border: 1.5px solid rgba(148,0,211,0.6);
  border-radius: 8px;
  color: white;
  font-family: 'Creepster', Arial, sans-serif;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { background: rgba(76,0,101,1); }
`;

const CaptionPreview = styled.div`
  font-size: 0.85rem;
  color: rgba(255,255,255,0.45);
  font-style: italic;
  padding: 8px 12px;
  background: rgba(148,0,211,0.08);
  border-radius: 6px;
  border-left: 3px solid rgba(148,0,211,0.4);
`;

const ClearCaptionBtn = styled.button`
  background: none;
  border: none;
  color: rgba(255,100,100,0.5);
  font-size: 0.8rem;
  cursor: pointer;
  text-align: left;
  padding: 0;
  &:hover { color: rgba(255,100,100,0.9); }
`;
